import { ArrowUpDown, Pencil } from 'lucide-react';
import styled from 'styled-components';

import type { ActionBarAction } from 'components/ActionBar';

import { Button } from '@/components/ui/button';
import { TotadminPermission } from 'data/__generated__';

type PlanningActionsToolbarProps = {
    // Edit-mode controls (regroupement multi-select) only make sense where stops are clickable — the Carte view.
    // On the table views this is false, so the toolbar shows only the column actions.
    showEditMode: boolean;
    editMode: boolean;
    onToggleEditMode: () => void;
    regroupementCount: number;
    onAssignAfterPoint: () => void;
    // « Réordonner dans l'ordre de sélection »: rewrites the selected stops' tour order to the click order.
    // Enabled only when at least one tour has ≥2 selected stops (a single stop can't change order).
    onReorderSelected: () => void;
    canReorder: boolean;
    assignTargetMode: boolean;
    columnActions: (ActionBarAction & { requiredPermissions?: readonly TotadminPermission[] })[];
};

// Shared horizontal action bar, rendered on every view. Hosts the column-selection actions; on the Carte view it
// also hosts the regroupement « mode édition » (multi-select + « Déplacer vers un arrêt… »).
export const PlanningActionsToolbar = ({
    showEditMode,
    editMode,
    onToggleEditMode,
    regroupementCount,
    onAssignAfterPoint,
    onReorderSelected,
    canReorder,
    assignTargetMode,
    columnActions,
}: PlanningActionsToolbarProps) => {
    const visibleColumnActions = columnActions.filter((action) => !action.hidden);

    // Off the map there's no edit-mode toggle, so with nothing selected there's nothing to show — stay out of the layout.
    if (!showEditMode && visibleColumnActions.length === 0) return null;

    return (
        <Bar>
            {showEditMode && (
                <Button
                    type="button"
                    size="sm"
                    variant={editMode ? 'default' : 'outline'}
                    onClick={onToggleEditMode}
                    data-test="planning-map-edit-mode"
                >
                    <Pencil size={15} />
                    {editMode ? 'Quitter le mode édition' : 'Mode édition'}
                </Button>
            )}

            {showEditMode && editMode
                ? !assignTargetMode && (
                      <>
                          <Button
                              type="button"
                              size="sm"
                              onClick={onAssignAfterPoint}
                              disabled={regroupementCount === 0}
                              data-test="planning-assign-after-point"
                              requiredPermissions={[TotadminPermission.DeliveryPlanningManage]}
                          >
                              Déplacer vers un arrêt…
                          </Button>
                          <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={onReorderSelected}
                              disabled={!canReorder}
                              data-test="planning-reorder-selected"
                              requiredPermissions={[TotadminPermission.DeliveryPlanningManage]}
                          >
                              <ArrowUpDown size={15} />
                              Réordonner dans l'ordre de sélection
                          </Button>
                          <Count>
                              {regroupementCount} regroupement{regroupementCount > 1 ? 's' : ''} sélectionné
                              {regroupementCount > 1 ? 's' : ''}
                          </Count>
                      </>
                  )
                : visibleColumnActions.map((action) => (
                      <Button
                          key={action.dataTest ?? action.label}
                          type="button"
                          size="sm"
                          variant={action.variant === 'destructive' ? 'destructive' : 'outline'}
                          onClick={action.onClick}
                          disabled={action.disabled}
                          data-test={action.dataTest}
                          requiredPermissions={action.requiredPermissions}
                      >
                          {action.icon}
                          {action.label}
                      </Button>
                  ))}
        </Bar>
    );
};

const Bar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const Count = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
`;
